import { useCallback } from 'react';

import { EntitySelect, type EntityOption } from '@/components/EntitySelect';
import { searchMemberUsers, resolveMemberUserLabel } from './select';

interface MemberSelectProps {
  /** Selected user id as a string; '' = nobody selected. */
  value: string;
  onChange: (value: string) => void;
  /** Limit candidates to staff members (default). Guests are rarely owners. */
  staffOnly?: boolean;
  /** Scope candidates to one project's members (id) — e.g. worklog Logger. */
  project?: number;
  /** Restrict candidates to these role titles (e.g. CRM owners → Founder/Sales). */
  roles?: readonly string[];
  placeholder?: string;
  /** Distinct react-query key when several member pickers share a screen. */
  queryKey?: string;
  disabled?: boolean;
  /** Show an inline clear (✕) to reset the selection — handy for filters. */
  clearable?: boolean;
  /** Override the trigger's classes (e.g. to size it tight on a filter bar). */
  triggerClassName?: string;
}

/**
 * Member/user picker for owner & assignment fields. Sources org-scoped members
 * from `/members/` and keys on the user id (backend owner FKs target User).
 */
export function MemberSelect({
  value,
  onChange,
  staffOnly = true,
  project,
  roles,
  placeholder = 'Select a member',
  queryKey = 'member-picker',
  disabled,
  clearable,
  triggerClassName,
}: MemberSelectProps) {
  // Stable identity so the search callback isn't recreated each render.
  const rolesKey = roles?.join(',');
  const search = useCallback(
    (query: string): Promise<EntityOption[]> =>
      searchMemberUsers(query, {
        ...(staffOnly ? { type: 'staff' as const } : {}),
        ...(project != null ? { project } : {}),
        ...(rolesKey ? { roles: rolesKey.split(',') } : {}),
      }),
    [staffOnly, project, rolesKey],
  );

  return (
    <EntitySelect
      // Fold the project into the key so switching project refetches (EntitySelect
      // keys only on queryKey + query, not the search closure).
      queryKey={project != null ? `${queryKey}-p${project}` : queryKey}
      value={value}
      onChange={onChange}
      search={search}
      resolveLabel={resolveMemberUserLabel}
      placeholder={placeholder}
      searchPlaceholder="Search members…"
      emptyText="No members found."
      disabled={disabled}
      clearable={clearable}
      triggerClassName={triggerClassName}
    />
  );
}
